

import axios from 'axios';
import { useEffect, useState } from 'react';
import CountUp from 'react-countup';
import {
  FiBriefcase,
  FiCalendar,
  FiTrendingUp,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
  FiUserX
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import './Dashboard.css';

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

const COLORS = [
  '#4F46E5',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316'
];

const SCORE_COLORS = {
  "0-40": "#EF4444",
  "40-50": "#F97316",
  "50-60": "#F59E0B",
  "60-70": "#EAB308",
  "70-80": "#84CC16",
  "80-90": "#10B981",
  "90-100": "#4F46E5"
};

// Custom Tooltip Components
const StatusTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="px-3 py-2 text-xs bg-white border border-gray-100 rounded-lg shadow-xl">
        <p className="font-bold text-gray-800 mb-0.5 leading-none">{data.name}</p>
        <p className="leading-none text-gray-500">Count: {data.value}</p>
      </div>
    );
  }
  return null;
};

const ScoreTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="px-3 py-2 text-xs bg-white border border-gray-100 rounded-lg shadow-xl">
        <p className="font-bold text-gray-800 mb-0.5 leading-none">{data.range}</p>
        <p className="leading-none text-gray-500">Candidates: {data.count}</p>
      </div>
    );
  }
  return null;
};

const RecruitmentDashboard = () => {
  const [stats, setStats] = useState({
    totalApplicants: 0,
    selected: 0,
    rejected: 0,
    interview: 0,
    statusBreakdown: [],
    monthlyTrend: [],
    scoreDistribution: [],
    qualityMetrics: {
      score50plus: 0,
      score60plus: 0,
      score70plus: 0,
      score80plus: 0,
      score90plus: 0
    },
    availableRoles: []
  });
  const [statusRole, setStatusRole] = useState("All");
  const [scoreRole, setScoreRole] = useState("All");
  const [tatRole, setTatRole] = useState("All");
  const [trendRole, setTrendRole] = useState("All");
  const [trendMonth, setTrendMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Get clientId from localStorage
  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  useEffect(() => {
    const clientId = getClientId();
    if (clientId) {
      fetchStats();
    } else {
      setLoading(false);
      setError("Please login to access recruitment dashboard");
    }
  }, [statusRole, scoreRole, tatRole, trendRole, trendMonth]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const clientId = getClientId();
      
      // API: GET /api/applications/stats/:clientId?statusRole=All&scoreRole=All&tatRole=All&trendRole=All
      const response = await axios.get(`${API_BASE_URL}/applications/stats/${clientId}`, {
        params: {
          statusRole: statusRole,
          scoreRole: scoreRole,
          tatRole: tatRole,
          trendRole: trendRole,
          ...(trendMonth ? { trendMonth } : {})
        }
      });
      
      if (response.data.success) {
        console.log("Recruitment Stats:", response.data.stats);
        setStats(response.data.stats);
      } else {
        setError(response.data.message || "Failed to load statistics");
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching recruitment stats:", err);
      setError(err.response?.data?.message || "Failed to load recruitment statistics");
      setLoading(false);
    }
  };

  // Calculate additional metrics
  const interviewToSelectedRate = stats.interview > 0
    ? ((stats.selected / stats.interview) * 100).toFixed(1)
    : 0;

  const rejectionRate = stats.totalApplicants > 0
    ? ((stats.rejected / stats.totalApplicants) * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="admin-dash">
        <div className="admin-dash__loading">
          <div className="admin-dash__spinner" />
          <p className="admin-dash__loading-text">Loading recruitment analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dash">
        <div className="admin-dash__error">
          <p className="admin-dash__error-title">Oops!</p>
          <p className="admin-dash__error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dash">
      <main className="">
        {/* Header */}
        <div className="admin-dash__header">
          <div>
            <h1 className="admin-dash__greeting">
              Recruitment <span>Dashboard</span>
            </h1>
            <p className="admin-dash__subtitle">
              Track applicants, interviews, and hiring metrics in one place.
            </p>
          </div>
          <div className="admin-dash__date-pill">
            <FiCalendar />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* 1. Top Summary Stats */}
        <div className="admin-dash__stats">
          <div className="admin-dash__stat" onClick={() => navigate("/job-applicants")}>
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Total Applicants</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--indigo">
                <FiUsers />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={stats.totalApplicants || 0} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">total applicants</div>
          </div>
          <div className="admin-dash__stat" onClick={() => navigate("/schedule-interviews")}>
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Interviews</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--amber">
                <FiUserPlus />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={stats.interview || 0} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">scheduled interviews</div>
          </div>
          <div className="admin-dash__stat" onClick={() => navigate("/selected-candidates")}>
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Selected</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--emerald">
                <FiUserCheck />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={stats.selected || 0} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">candidates selected</div>
          </div>
          <div className="admin-dash__stat" onClick={() => navigate("/rejected-candidates")}>
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Rejected</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--rose">
                <FiUserX />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={stats.rejected || 0} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">candidates rejected</div>
          </div>
          <div className="admin-dash__stat" onClick={() => navigate("/job-positions-status")}>
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Total Vacancies</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--cyan">
                <FiBriefcase />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={stats.totalVacancies || 0} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">open positions</div>
          </div>
        </div>

        {/* 2. Charts Section */}
        <div className="admin-dash__charts-grid">
          {/* Status Distribution - Pie Chart */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Status Distribution</h3>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="admin-dash__month-input"
                  value={statusRole}
                  onChange={(e) => setStatusRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  {stats.availableRoles?.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <button
                  onClick={() => navigate("/job-applicants")}
                  className="admin-dash__card-link hidden sm:block"
                >
                  View All →
                </button>
              </div>
            </div>

            <div className="admin-dash__card-body flex-1">
              {stats.statusBreakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.statusBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={false}
                    >
                      {stats.statusBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<StatusTooltip />} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      wrapperStyle={{ fontSize: '10px', maxWidth: '40%' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="admin-dash__empty-chart">No status data available</div>
              )}
            </div>
          </div>

          {/* Score Distribution - Bar Chart */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Score Distribution</h3>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="admin-dash__month-input"
                  value={scoreRole}
                  onChange={(e) => setScoreRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  {stats.availableRoles?.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <button
                  onClick={() => navigate("/job-applicants")}
                  className="admin-dash__card-link hidden sm:block"
                >
                  View Details →
                </button>
              </div>
            </div>

            <div className="admin-dash__card-body flex-1">
              {stats.scoreDistribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.scoreDistribution} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="range"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 11 }}
                      angle={-25}
                      textAnchor="end"
                      interval={0}
                      height={60}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 11 }}
                    />
                    <Tooltip content={<ScoreTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={25}>
                      {stats.scoreDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={SCORE_COLORS[entry.range] || "#4F46E5"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="admin-dash__empty-chart">No score data available</div>
              )}
            </div>
          </div>
        </div>

        {/* 3. Quality Metrics & Hiring TAT */}
        <div className="admin-dash__charts-grid">
          {/* Quality Metrics Cards */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Quality Metrics</h3>
              </div>
              <FiTrendingUp className="text-xl text-gray-400" />
            </div>

            <div className="admin-dash__card-body">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                <QualityCard
                  label="50+ Score"
                  value={stats.qualityMetrics?.score50plus || 0}
                  total={stats.totalApplicants}
                  color="amber"
                />
                <QualityCard
                  label="60+ Score"
                  value={stats.qualityMetrics?.score60plus || 0}
                  total={stats.totalApplicants}
                  color="emerald"
                />
                <QualityCard
                  label="70+ Score"
                  value={stats.qualityMetrics?.score70plus || 0}
                  total={stats.totalApplicants}
                  color="cyan"
                />
                <QualityCard
                  label="80+ Score"
                  value={stats.qualityMetrics?.score80plus || 0}
                  total={stats.totalApplicants}
                  color="indigo"
                />
                <QualityCard
                  label="90+ Score"
                  value={stats.qualityMetrics?.score90plus || 0}
                  total={stats.totalApplicants}
                  color="purple"
                />
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Interview to Selection Rate</span>
                  <span className="font-bold text-emerald-600">{interviewToSelectedRate}%</span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-500">Overall Rejection Rate</span>
                  <span className="font-bold text-rose-600">{rejectionRate}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Hiring TAT (Turnaround Time) */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Hiring TAT (Days)</h3>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="admin-dash__month-input"
                  value={tatRole}
                  onChange={(e) => setTatRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  {stats.availableRoles?.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <button
                  onClick={() => navigate("/job-applicants")}
                  className="admin-dash__card-link hidden sm:block"
                >
                  View Details →
                </button>
              </div>
            </div>

            <div className="admin-dash__card-body flex-1 overflow-x-auto">
              {stats.tatDistribution?.length > 0 ? (
                <div style={{ minWidth: Math.max(stats.tatDistribution.length * 50, 400) }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.tatDistribution} margin={{ top: 10, right: 10, left: -20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 10 }}
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(value) => [`${value} Days`, 'Hiring TAT']}
                      />
                      <Bar dataKey="days" radius={[4, 4, 0, 0]} barSize={25}>
                        {stats.tatDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="admin-dash__empty-chart">No TAT data available</div>
              )}
            </div>
          </div>
        </div>

        {/* 4. Monthly Trend */}
        <div className="admin-dash__card admin-dash__chart-wrap--tall">
          <div className="admin-dash__card-header">
            <div>
              <h3 className="admin-dash__card-title">Monthly Application Trend</h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                className="admin-dash__month-input"
                value={trendRole}
                onChange={(e) => setTrendRole(e.target.value)}
              >
                <option value="All">All Roles</option>
                {stats.availableRoles?.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <input
                type="month"
                className="admin-dash__month-input"
                value={trendMonth}
                onChange={(e) => setTrendMonth(e.target.value)}
                title="Filter by month for daily view"
              />
              {trendMonth && (
                <button
                  onClick={() => setTrendMonth("")}
                  className="text-[10px] font-bold text-gray-400 hover:text-rose-500 transition-colors"
                  title="Clear month filter"
                >
                  ✕ Clear
                </button>
              )}
              <button
                onClick={() => navigate("/job-applicants")}
                className="admin-dash__card-link hidden sm:block"
              >
                View All →
              </button>
            </div>
          </div>

          <div className="admin-dash__card-body flex-1">
            {stats.monthlyTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.monthlyTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    formatter={(value) => [`${value} Applications`, 'Count']}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    fill="url(#colorApplications)"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="admin-dash__empty-chart">No trend data available</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// Quality Card component for metrics
const QualityCard = ({ label, value, total, color }) => {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  const colorClasses = {
    amber: "bg-amber-50 border-amber-100",
    emerald: "bg-emerald-50 border-emerald-100",
    cyan: "bg-cyan-50 border-cyan-100",
    indigo: "bg-indigo-50 border-indigo-100",
    purple: "bg-purple-50 border-purple-100",
  };

  return (
    <div className={`p-2 px-3 rounded-xl border ${colorClasses[color] || colorClasses.indigo} shadow-sm`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-xl font-black text-gray-800">
          <CountUp end={value} duration={1.5} />
        </span>
        <span className="text-[10px] font-bold text-gray-600">{percentage}%</span>
      </div>
      <div className="w-full h-1 bg-white rounded-full mt-2 overflow-hidden">
        <div
          className={`h-full rounded-full ${color === 'amber' ? 'bg-amber-500' :
            color === 'emerald' ? 'bg-emerald-500' :
              color === 'cyan' ? 'bg-cyan-500' :
                color === 'purple' ? 'bg-purple-500' :
                  'bg-indigo-500'
            }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default RecruitmentDashboard;